import { Tooltip } from '@src/components/Tooltip';
import { useDynamicCallback } from '@src/hooks/useDynamicCallback';
import { debounce, isEqual } from 'lodash-es';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { Wrapper } from './styles';

export type SliderProps =
  | {
      value: number;
      onChange: (value: number | number[]) => void;
      useRange?: false;
      min?: number;
      max?: number;
      reverse?: boolean;
      disabled?: boolean;
    }
  | {
      value: number[];
      onChange: (value: number | number[]) => void;
      useRange: true;
      min?: number;
      max?: number;
      reverse?: boolean;
      disabled?: boolean;
    };

export const handleRender = (node: ReactNode, props: { value: ReactNode }) => {
  return <Tooltip tooltip={props.value} children={node} placement="top" trigger="hover" />;
};

// TODO: this is an extremely basic first pass needed to unblock the multileg payoff charts
// further work is needed for example to support various sizes, states, etc.
export const Slider = (props: SliderProps) => {
  const theme = useTheme();
  const { value, useRange, min, max, reverse, disabled, onChange } = props;
  const [stateValue, setStateValue] = useState(value);
  const [isChanging, setIsChanging] = useState(false);

  const debouncedHandleChange = useMemo(() => debounce(onChange, 20), [onChange]);

  useEffect(() => {
    if (isChanging) {
      return;
    }
    if (value instanceof Array && !isEqual(value, stateValue)) {
      setStateValue(value);
    } else if (value !== stateValue) {
      setStateValue(value);
    }
  }, [value, stateValue, isChanging]);

  const handleChange = useDynamicCallback((value: number | number[]) => {
    if (!isChanging) {
      setIsChanging(true);
    }
    setStateValue(value);
    debouncedHandleChange(value);
  });

  return (
    <Wrapper
      className={reverse && stateValue === max ? 'reverse' : ''}
      range={useRange}
      allowCross={false}
      onChange={handleChange}
      onChangeComplete={() => setIsChanging(false)}
      value={stateValue}
      min={min}
      reverse={reverse}
      max={max}
      disabled={disabled}
      handleRender={handleRender}
      railStyle={{ background: theme.colors.gray['040'] }}
      trackStyle={{ background: theme.backgroundPrimaryButton }}
      handleStyle={{
        borderRadius: theme.borderRadiusButtonDefault,
        opacity: 1,
        border: `1px solid ${theme.colors.gray['040']}`,
        background: theme.colors.gray['030'],
      }}
    />
  );
};
